import React, { FC, useMemo } from 'react';
import { ProfilesRecord } from '../Page/types/ProfilesRecord';
import { Link, RadioGroup, Switch, TextInput } from '@gravity-ui/uikit';
import { MAX_VIDEO_TITLE_LENGTH } from '../../constants/MAX_VIDEO_TITLE_LENGTH';
import classes from './styles/VideoInfoForm.module.css';
import { QuestionIconPopover } from '../ui/QuestionIconPopover/QuestionIconPopover';
import { getProfileOptions, getDefaultProfile } from '../../libs/utils/getProfileOptions';
import { ThumbnailUpload, ThumbnailChange } from '../ThumbnailUpload/ThumbnailUpload';
import { formsT } from '../../i18n';

type VideoInfoForm = {
    profile?: string;
    profiles: ProfilesRecord;
    name: string;
    enableSummarization?: boolean;
    placeholder?: string;
    isCompact?: boolean;
    padding?: string;
    onChangeName: (value: string) => void;
    onChangeProfile: (value: string) => void;
    onToggleSummarization?: () => void;
    disabled: boolean;
    thumbnailProps?: {
        currentThumbnailId?: string;
        onChange: (change: ThumbnailChange) => void;
    };
};

const getFormElementClassNames = (isCompact?: boolean) =>
    `${classes['form-element']} ${!isCompact ? classes['form-element--wide'] : ''}`;

export const VideoInfoForm: FC<VideoInfoForm> = ({
    profile,
    profiles,
    name,
    enableSummarization,
    placeholder = '',
    isCompact,
    padding,
    onChangeName,
    onChangeProfile,
    onToggleSummarization,
    disabled,
    thumbnailProps,
}) => {
    const canEnableSummarization =
        enableSummarization !== undefined && onToggleSummarization !== undefined;

    const radioGroupOptions = useMemo(() => getProfileOptions(profiles), [profiles]);

    const currentProfile = profile || getDefaultProfile(profiles);

    const validationTitleMessage = useMemo<string | undefined>(() => {
        if (!name.length && !placeholder) {
            return formsT('titleCannotBeEmpty');
        }

        if (name.length > MAX_VIDEO_TITLE_LENGTH) {
            return formsT('titleMaxLength', { maxLength: MAX_VIDEO_TITLE_LENGTH });
        }
    }, [name]);

    return (
        <>
            <div
                className={getFormElementClassNames(isCompact)}
                style={padding ? { paddingLeft: padding, paddingRight: padding } : {}}
            >
                <label className={classes['form-element__label']}>
                    {isCompact ? formsT('profile') : formsT('selectProfile')}
                    <span className="required">*</span>
                    <QuestionIconPopover
                        popoverContent={
                            <Link
                                href="https://wiki.yandex-team.ru/vp/users/nda-video/#profili"
                                target="_blank"
                            >
                                {formsT('profilesInfo')}
                            </Link>
                        }
                        style={{
                            position: 'relative',
                            top: '4px',
                        }}
                    />
                </label>
                <RadioGroup
                    className={classes['form-element__radio-group']}
                    direction="vertical"
                    options={radioGroupOptions}
                    value={currentProfile}
                    onUpdate={onChangeProfile}
                    disabled={disabled}
                />
            </div>
            <div className={getFormElementClassNames(isCompact)} style={padding ? { padding } : {}}>
                <label className={classes['form-element__label']} htmlFor={'title-input'}>
                    {isCompact ? formsT('title') : formsT('enterVideoTitle')}
                    <span className="required">*</span>
                </label>
                <TextInput
                    id={'title-input'}
                    onUpdate={onChangeName}
                    value={name}
                    placeholder={placeholder}
                    validationState={name.length > MAX_VIDEO_TITLE_LENGTH ? 'invalid' : undefined}
                    error={validationTitleMessage}
                    errorMessage={validationTitleMessage}
                    errorPlacement={'outside'}
                    disabled={disabled}
                />
            </div>
            {canEnableSummarization && (
                <div
                    className={getFormElementClassNames(isCompact)}
                    style={padding ? { padding } : {}}
                >
                    <label className={classes['form-element__label']} htmlFor={'title-input'}>
                        {isCompact ? formsT('summarization') : formsT('enableSummarization')}
                    </label>
                    <Switch
                        checked={enableSummarization}
                        onChange={onToggleSummarization}
                        disabled={disabled}
                    />
                </div>
            )}
            {thumbnailProps && (
                <div style={padding ? { padding } : {}}>
                    <ThumbnailUpload
                        currentThumbnailId={thumbnailProps.currentThumbnailId}
                        onChange={thumbnailProps.onChange}
                        disabled={disabled}
                        compact={isCompact}
                    />
                </div>
            )}
        </>
    );
};
